import { Registry } from '@react-registry'
import { deserialize as defaultDeserialize } from './deserializer/defaultDeserializer'
import { deserialize as googleDeserialize } from './deserializer/googleDocsSerializer'
import { deserialize as csDeserialize } from './deserializer/superchargedRTEDeserializer'
import { deserialize as wordDeserialize } from './deserializer/wordDeserializer'
import { deserialize as onlineWordDeserialize } from './deserializer/onlineWordDeserializer'
import collapse from '../../../../../../utils/collapse-whitespace'
import { preInsert, preInsertTable } from './utils'
import { getRegistry } from '../../../utils/getRegistry'
import { detect } from 'detect-browser'
import { uploadImage } from '../../Element/contentstack/image/request'

import { Editor, Transforms, Range } from 'slate'
import { transformInvalidNestedLists } from '../../../utils/serialize/jsonHtmlTransformations'
const pattern = /^docs-internal-guid/

const getUserAgentDetails = () => {
  const systemDetails = detect()
  return {
    platform: systemDetails.os,
    browser: systemDetails.name,
    browserVersion: systemDetails.version
  }
}

const getCustomInserts = () => {
  return Object.entries(getRegistry('v2.withPreInsert')).map(
    ([key, value]) => {
      return [key, value['components'][0]['component']]
    }
  )
}
const getFragmentFromCopyPasteScript = (
  copyPasteConfig,
  docTypeFromScript,
  parsedHtml,
  wordPasteOptions,
  editor
) => {
  try {
    const userAgentDetails = getUserAgentDetails()
    const docCopyPasteConfig = copyPasteConfig?.docCopyPasteConfig
    let configMatch = Array.from(docCopyPasteConfig.config).find((config: any) => {
      return (
        config.type === docTypeFromScript.docType &&
        userAgentDetails.browser.includes(config?.browser) &&
        userAgentDetails.platform.includes(config?.platform) &&
        config?.mode === docTypeFromScript.mode &&
        config.shouldVisit(parsedHtml)
      )
    })
    if (configMatch) {
      //@ts-ignore
      return configMatch.parser(parsedHtml, wordPasteOptions, editor)
    }
    return null
  } catch (err) {
    console.log('Error while copy pasting data using external script', err)
  }
}

const uploadImageToAsset = async (editor, url, extraAttrs = {}) => {
  if (url.startsWith('data')) { // upload to assets
    let fileArray = url.split(','),
      mime = fileArray[0].match(/:(.*?);/)[1],
      bstr = atob(fileArray[1]),
      n = bstr.length,
      u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    const fileToUpload = new File([u8arr], 'image.png', { type: mime });
    const requestProps: Object = editor?.requestProps || {};
    const folderUid = ''
    const imgUploadRes: any = await uploadImage({ ...requestProps, files: [fileToUpload], folderUid })

    if (imgUploadRes) {
      return imgUploadRes?.[0]?.['asset']?.['url']
    }
  }
}

const convertBase64UrlToAssetUrl = async (editor, parsedHtml) => {
  const imgElements = parsedHtml.querySelectorAll('img')
  if (imgElements) {
    await Promise.all(Array.from(imgElements).map(async (img: any) => {
      if (img.src?.startsWith('data')) {
        const response = await uploadImageToAsset(editor, img.src);
        if (response) {
          img.setAttribute('src', response);
        }
      }
    }));
  }
  return parsedHtml
}

export const removeEmptyLinesFromTextAndConvertIntoParagraph = (text: string) => {
  // Collapse multiple new lines into a single new line
  const cleanedText = text.replace(/\n{2,}/g, '\n')
  const lines = cleanedText.split('\n').filter(line => line.trim() !== '');
  const nodes = lines.map(line => ({
    type: 'p',
    children: [{ text: line }],
  }));
  return nodes;
}

const withHtml = (editor) => {
  const { insertData } = editor

  editor.insertData = async (data) => {
    const customPasteOptions = editor.customPasteOptions || {}
    const copyPasteConfig = editor.copyPasteConfig || {}
    const googlePasteOptions = (customPasteOptions && customPasteOptions.googleDoc) || {}
    const wordPasteOptions = (customPasteOptions && customPasteOptions.wordDoc) || {}
    const html = data.getData('text/html');
    const dataTypes = data.types;
    if (html) {
      let parsedHtml: any = new DOMParser().parseFromString(html, 'text/html').body
      collapse(parsedHtml)
      transformInvalidNestedLists(parsedHtml);
      let docTypeFromScript = copyPasteConfig?.findDocType?.(parsedHtml)

      let fragment
      let googlePlainHtml = ''

      let isGoogleDoc = Array.from(parsedHtml.childNodes).some((node: any) => {
        if (node.nodeName === 'B' && pattern.test(node['id'])) {
          googlePlainHtml = node.innerHTML
          return true
        }
        return false
      })
      const isCSContent = parsedHtml.querySelector('[data-editor-type*="scrte"]')

      const isWord =
        parsedHtml.getElementsByTagName('O:P').length ||
        parsedHtml.querySelector('[class*="Mso"]' || parsedHtml.querySelector('[style*="mso"]'))

      // MS Word Online uses different markers than desktop Word
      const isWordOnline =
        parsedHtml.querySelector('[class*="ListContainerWrapper"]') ||
        parsedHtml.querySelector('[class*="BulletListStyle"]') ||
        parsedHtml.querySelector('[class*="NumberListStyle"]')
      
      console.log('[withHtml] Content detection:', {
        isGoogleDoc,
        isWord,
        isWordOnline,
        htmlPreview: parsedHtml.innerHTML.substring(0, 500)
      })

      let shouldGetContentFromSlateFragment = false
      let slateFragmentData = null

      try {
        const slateFragmentElement = parsedHtml.querySelector('[data-slate-fragment]')
        if (slateFragmentElement) {
          slateFragmentData = JSON.parse(decodeURIComponent(atob(slateFragmentElement.getAttribute('data-slate-fragment'))))[0]
          shouldGetContentFromSlateFragment = slateFragmentData?.children?.length === 1
          while (slateFragmentData.children.length < 2 && slateFragmentData.children[0].children) {
            slateFragmentData = slateFragmentData.children[0]
          }
          if (slateFragmentData.type === 'fragment') {
            slateFragmentData = slateFragmentData.children
          }
          if (!Array.isArray(slateFragmentData)) {
            slateFragmentData = [slateFragmentData]
          }
        }
      }
      catch (err) {
        shouldGetContentFromSlateFragment = false
        console.error("Error while parsing slate fragment data", err)
      }
      const fragmentFromCopyPasteScript = getFragmentFromCopyPasteScript(
        copyPasteConfig,
        docTypeFromScript,
        parsedHtml,
        wordPasteOptions,
        editor
      )

      // Pasted base64 images must become Contentstack assets on every paste path
      // (Word, Word Online, default) — not only the default deserializer. Only await
      // when a data URI is actually present, so other pastes stay synchronous.
      const hasBase64Image = Array.from(parsedHtml.querySelectorAll('img')).some(
        (img: any) => img.src?.startsWith('data')
      )
      if (hasBase64Image) {
        parsedHtml = await convertBase64UrlToAssetUrl(editor, parsedHtml)
      }

      if (fragmentFromCopyPasteScript) {
        fragment = fragmentFromCopyPasteScript
      } else if (isGoogleDoc) {
        parsedHtml = new DOMParser().parseFromString(googlePlainHtml, 'text/html').body
        fragment = googleDeserialize(parsedHtml, googlePasteOptions)
      }
      else if (shouldGetContentFromSlateFragment) {
        fragment = slateFragmentData

      } else if (isCSContent) {
        fragment = csDeserialize(parsedHtml)
      } else if (isWordOnline) {
        console.log('[withHtml] Detected MS Word Online content, using onlineWordDeserialize')
        fragment = onlineWordDeserialize(parsedHtml, wordPasteOptions, editor)
        console.log('[withHtml] Fragment from onlineWordDeserialize:', JSON.stringify(fragment, null, 2))
      } else if (isWord) {
        console.error('Error while loading word copy paste script. Running deserilizer from venus-components.')
        fragment = wordDeserialize(parsedHtml, wordPasteOptions, editor)
      } else {
        fragment = defaultDeserialize(parsedHtml, editor)
      }
      if (fragment.length > 1){
        fragment = preInsert(editor, fragment);
        let customInserts = getCustomInserts();
        if (customInserts?.length) {
          customInserts.forEach((element) => {
            try {
              fragment = element[1](editor, fragment)
              Array.isArray(fragment)
              fragment.every((element) => typeof element === 'object')
            }
            catch (err) {
              console.log("Invalid Fragment: ", err)
            }
          })
        }
      }
      else if (fragment.length === 1 && fragment[0].type === "table" && Array.isArray(editor.children)) {
        preInsertTable(fragment, editor.children?.[0].children?.length)
      }
      Transforms.insertFragment(editor, fragment)
      return
    } else if (dataTypes && Array.isArray(dataTypes) && dataTypes.includes('text/plain') && dataTypes.length === 1) {
      const text = data.getData('text/plain');
      if (text.length > 0) {
        const nodes = removeEmptyLinesFromTextAndConvertIntoParagraph(text);
        if (!nodes?.length) return;
        let customInserts = getCustomInserts();
        if (customInserts?.length) {
          customInserts.forEach((element) => {
            const newNodes = element[1](editor, nodes);
            if (newNodes.some(node => node.type === 'p')) {
              Transforms.insertFragment(editor, newNodes);
            } else {
              const codeBlock = {
                type: 'code',
                children: newNodes
              }
              Transforms.insertFragment(editor, [codeBlock]);
            }
          })
          return;
        }
        const cellType = ['td', 'th']
        const { selection } = editor;
        const selectedCells = Array.from(Editor.nodes(editor, {
          match: n => cellType.includes((n as any).type),
          mode: 'lowest'
        }))
        if (selection && !Range.isCollapsed(selection) && selectedCells.length > 1) {
          Transforms.collapse(editor, { edge: 'start' });
        }
        return Transforms.insertFragment(editor, nodes);
      }
    }

    insertData(data)
  }
  return editor
}

Registry.register(withHtml, { id: 'withHtml', registry: 'plugin' })
